import React, { useState, useCallback } from 'react';
import Cropper, { Point, Area } from 'react-easy-crop';
import { X, Check, RotateCcw, ZoomIn, Maximize, Loader } from 'lucide-react';

interface PhotoCropperProps {
  image: string;
  aspect: number;
  onCropComplete: (croppedImage: string) => void;
  onCancel: () => void;
}

const PhotoCropper: React.FC<PhotoCropperProps> = ({ image, aspect, onCropComplete, onCancel }) => {
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const onCropChange = (crop: Point) => {
    setCrop(crop);
  };

  const onZoomChange = (zoom: number) => {
    setZoom(zoom);
  };

  const onCropAreaComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      if (!url.startsWith('data:')) {
        image.setAttribute('crossOrigin', 'anonymous');
      }
      image.src = url;
    });

  const getCroppedImg = async (
    imageSrc: string,
    pixelCrop: Area,
    rotation = 0
  ): Promise<string> => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) return '';

    const rotRad = (rotation * Math.PI) / 180;
    const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
      image.width,
      image.height,
      rotation
    );

    // Set canvas size to the bounding box of the rotated image
    canvas.width = bBoxWidth;
    canvas.height = bBoxHeight;

    // Translate to center and rotate
    ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
    ctx.rotate(rotRad);
    ctx.translate(-image.width / 2, -image.height / 2);

    // Draw the full rotated image
    ctx.drawImage(image, 0, 0);

    // Create a second canvas for the actual crop
    const cropCanvas = document.createElement('canvas');
    const cropCtx = cropCanvas.getContext('2d');

    if (!cropCtx) return '';

    cropCanvas.width = pixelCrop.width;
    cropCanvas.height = pixelCrop.height;

    // Draw the cropped area from the first canvas to the second one
    cropCtx.drawImage(
      canvas,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    // Final compression for storage limits (keep under ~300KB)
    let quality = 0.85;
    let dataUrl = cropCanvas.toDataURL('image/jpeg', quality);
    
    // Iteratively reduce quality if file is too large for Firestore
    while (dataUrl.length > 300000 && quality > 0.3) {
      quality -= 0.1;
      dataUrl = cropCanvas.toDataURL('image/jpeg', quality);
    }

    return dataUrl;
  };

  const rotateSize = (width: number, height: number, rotation: number) => {
    const rotRad = (rotation * Math.PI) / 180;
    return {
      width:
        Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
      height:
        Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
    };
  };

  const handleDone = async () => {
    if (croppedAreaPixels) {
      setIsProcessing(true);
      try {
        const croppedImage = await getCroppedImg(image, croppedAreaPixels, rotation);
        onCropComplete(croppedImage);
      } catch (e) {
        console.error("Crop error:", e);
        alert("Failed to crop image. Please try a different photo.");
      } finally {
        setIsProcessing(false);
      }
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col">
      <div className="flex justify-between items-center p-4 bg-gray-900 border-b border-gray-800">
        <button 
          onClick={onCancel} 
          disabled={isProcessing}
          className="text-white flex items-center gap-1 font-bold disabled:opacity-50"
        >
          <X size={20} /> Cancel
        </button>
        <h3 className="text-white font-black text-lg uppercase tracking-tight">Crop Profile Photo</h3>
        <button 
          onClick={handleDone} 
          disabled={isProcessing}
          className="bg-emerald-600 text-white px-5 py-2.5 rounded-xl font-black flex items-center gap-2 shadow-lg shadow-emerald-900/40 disabled:opacity-50 active:scale-95 transition-transform"
        >
          {isProcessing ? (
            <>
              <Loader className="animate-spin" size={20} /> PROCESSING
            </>
          ) : (
            <>
              <Check size={20} /> DONE
            </>
          )}
        </button>
      </div>

      <div className="relative flex-1 bg-gray-950 overflow-hidden">
        <Cropper
          image={image}
          crop={crop}
          zoom={zoom}
          rotation={rotation}
          aspect={aspect}
          onCropChange={onCropChange}
          onZoomChange={onZoomChange}
          onRotationChange={setRotation}
          onCropComplete={onCropAreaComplete}
        />
      </div>

      <div className="p-6 bg-gray-900 border-t border-gray-800 space-y-6">
        <div className="space-y-3">
          <div className="flex justify-between items-center px-1">
             <label className="text-gray-400 text-[10px] font-black uppercase tracking-widest flex items-center gap-1">
               <ZoomIn size={12} /> Zoom Level
             </label>
             <span className="text-emerald-500 font-mono text-xs">{zoom.toFixed(1)}x</span>
          </div>
          <input
            type="range"
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            aria-labelledby="Zoom"
            onChange={(e) => setZoom(Number(e.target.value))}
            className="w-full h-2 bg-gray-800 rounded-lg appearance-none cursor-pointer accent-emerald-500"
          />
        </div>

        <div className="space-y-3">
          <div className="flex justify-between items-center px-1">
             <label className="text-gray-400 text-[10px] font-black uppercase tracking-widest flex items-center gap-1">
               <RotateCcw size={12} /> Rotation
             </label>
             <span className="text-emerald-500 font-mono text-xs">{rotation}°</span>
          </div>
          <input
            type="range"
            value={rotation}
            min={0}
            max={360}
            step={1}
            aria-labelledby="Rotation"
            onChange={(e) => setRotation(Number(e.target.value))}
            className="w-full h-2 bg-gray-800 rounded-lg appearance-none cursor-pointer accent-emerald-500"
          />
        </div>

        <div className="flex justify-center pt-2">
           <p className="text-[10px] text-gray-500 italic text-center leading-relaxed">
             <Maximize size={10} className="inline mr-1"/>
             Pinch or drag to position the photo within the frame.<br/>
             This ensures it fits perfectly on the ID card.
           </p>
        </div>
      </div>
    </div>
  );
};

export default PhotoCropper;
